/**
 * Owner-side booking rules the admin UI applies before calling the API.
 * The backend enforces every one of these again; these only decide which
 * actions to offer and what to warn about.
 *
 * @module bookingRules
 */
import { ACTIVE_STATUSES, CANCELLED_BY } from "../../services/api/adminService";
import { PRACTICE } from "../../data/practice";

const HOUR_MS = 60 * 60 * 1000;

/** Longest session link the backend stores. */
export const MAX_MEETING_URL_LENGTH = 512;

const instant = (iso) => {
  const ms = typeof iso === "string" ? Date.parse(iso) : NaN;
  return Number.isFinite(ms) ? ms : null;
};

const nowMs = (now) => (now instanceof Date ? now.getTime() : typeof now === "number" ? now : Date.now());

/** Scheduled or confirmed (can still be cancelled, completed, marked no-show). */
export const isActive = (booking) => Boolean(booking) && ACTIVE_STATUSES.includes(booking.status);

/** True once the session's start time has passed. Unknown start: false. */
export const hasStarted = (booking, now) => {
  const start = instant(booking && booking.start_time);
  return start !== null && start <= nowMs(now);
};

/** True once the session's end (or, without one, its start) has passed. */
export const hasEnded = (booking, now) => {
  const end = instant(booking && booking.end_time);
  const at = end !== null ? end : instant(booking && booking.start_time);
  return at !== null && at <= nowMs(now);
};

/**
 * Complete / No-show are offered only for an active booking whose start has
 * passed (backend: 409 invalid_state for a future session).
 */
export const canMarkOutcome = (booking, now) => isActive(booking) && hasStarted(booking, now);

/** "Add session link" is offered on an active booking that has not ended. */
export const canSetMeetingUrl = (booking, now) => isActive(booking) && !hasEnded(booking, now);

/**
 * True when now is less than CANCELLATION_WINDOW_HOURS before the start
 * (including after the start). Unknown start: false.
 */
export const insideCancellationWindow = (booking, now, policy = PRACTICE) => {
  const start = instant(booking && booking.start_time);
  return start !== null && start - nowMs(now) < policy.cancellationWindowHours * HOUR_MS;
};

/**
 * Whether cancelling now, for this actor, owes the late-cancellation fee.
 * Mirrors the backend: only a client-requested cancellation less than
 * CANCELLATION_WINDOW_HOURS before the start, with a fee above zero.
 *
 * @param {object} booking - admin booking
 * @param {'client'|'practice'} cancelledBy
 * @param {Date|number} [now]
 * @param {object} [policy] - {cancellationWindowHours, lateCancelFeePercent}
 * @returns {boolean}
 */
export const lateFeeApplies = (booking, cancelledBy, now, policy = PRACTICE) => {
  if (cancelledBy !== CANCELLED_BY.CLIENT) return false;
  const fee = Number(booking && booking.fee_amount);
  if (!(fee > 0) || !(policy.lateCancelFeePercent > 0)) return false;
  return insideCancellationWindow(booking, now, policy);
};

/** The late fee in dollars for a booking (fee x LATE_CANCEL_FEE_PERCENT). */
export const lateFeeAmount = (booking, policy = PRACTICE) => {
  const fee = Number(booking && booking.fee_amount);
  if (!(fee > 0)) return 0;
  return Math.round(fee * policy.lateCancelFeePercent) / 100;
};

/**
 * Client-side check of a session link, matching the backend rules:
 * https only, a host with a dot, no user name or password, no whitespace
 * or control characters, at most 512 characters. Trimmed.
 *
 * @param {unknown} value
 * @returns {{url: string, error: null} | {url: null, error: string}}
 */
export const validateMeetingUrl = (value) => {
  const fail = (error) => ({ url: null, error });
  const url = typeof value === "string" ? value.trim() : "";
  if (!url) return fail("Enter the session link.");
  if (url.length > MAX_MEETING_URL_LENGTH) {
    return fail(`The link must be at most ${MAX_MEETING_URL_LENGTH} characters.`);
  }
  // eslint-disable-next-line no-control-regex
  if (/[\s\u0000-\u001f\u007f]/.test(url)) return fail("The link must not contain spaces.");
  let parsed;
  try {
    parsed = new URL(url);
  } catch (error) {
    return fail("Enter a full link starting with https://");
  }
  if (parsed.protocol !== "https:") return fail("The link must start with https://");
  if (!parsed.hostname || !parsed.hostname.includes(".")) {
    return fail("Enter a full link starting with https://");
  }
  if (parsed.username || parsed.password || /^https:\/\/[^/?#]*@/i.test(url)) {
    return fail("The link must not contain a user name or password.");
  }
  return { url, error: null };
};
